/**
 * Native Push Notification Service
 * Handles iOS/Android native notifications via Capacitor
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import api from './api';

// Chat-Mitteilung? iOS liefert die Push-Daten (type) mit; Android bei vom
// System angezeigten FCM-Mitteilungen nicht immer, dann hilft der Titel
// ("Neue Nachricht von …", notificationService.sendChatNotification).
const CHAT_TITLE_PREFIX = 'Neue Nachricht von ';
const isChatNotification = (notification) => {
  const data = notification?.data || {};
  return data.type === 'CHAT_MESSAGE'
    || (typeof notification?.title === 'string' && notification.title.startsWith(CHAT_TITLE_PREFIX));
};

class PushNotificationService {
  constructor() {
    this.isInitialized = false;
    this.deviceToken = null;
    this.initPromise = null;
    // Zählt jedes cleanup() hoch: Eine Initialisierung, die vor dem Logout begann,
    // darf danach nicht mehr "initialisiert" melden (ihre Listener sind weg).
    this.generation = 0;
    // Laufendes removeAllListeners() aus cleanup(): Die nächste Initialisierung wartet
    // darauf, sonst entfernt es die Listener des nächsten Kontos gleich wieder.
    this.listenerRemoval = null;
    // Registrierungen nacheinander ans Backend: Das Gerät gehört immer dem zuletzt
    // gemeldeten Konto, auch wenn zwei Anmeldungen kurz hintereinander kommen.
    this.registrationQueue = Promise.resolve();
  }

  /**
   * Initialize Push Notifications
   * Call this after user login
   */
  async initialize() {
    // Only run on native platforms (iOS/Android)
    if (!Capacitor.isNativePlatform()) {
      console.log('PushNotifications: Not a native platform, skipping initialization');
      return;
    }

    if (this.isInitialized) {
      console.log('PushNotifications: Already initialized');
      return;
    }

    // App.js ruft initialize() beim Start von zwei Stellen fast gleichzeitig auf
    // (localStorage-Pfad und user-Effekt). isInitialized wird erst nach zwei awaits
    // gesetzt, also kamen beide Aufrufe durch den Guard: jeder hängte vier Listener
    // an und schickte den Token mehrfach ans Backend, und ein Tipp auf die
    // Mitteilung löste die Navigation mehrfach aus. Läuft schon eine
    // Initialisierung, teilen sich alle Aufrufer dasselbe Promise.
    if (this.initPromise) {
      return this.initPromise;
    }

    const initPromise = this._initialize().finally(() => {
      // Nur das eigene Promise abräumen: cleanup() kann inzwischen eine neue
      // Initialisierung (nächstes Konto) eingetragen haben.
      if (this.initPromise === initPromise) this.initPromise = null;
    });
    this.initPromise = initPromise;
    return initPromise;
  }

  async _initialize() {
    const generation = this.generation;
    try {
      if (this.listenerRemoval) {
        await this.listenerRemoval;
        if (generation !== this.generation) return;
      }

      // IMPORTANT: Set up listeners BEFORE calling register()
      // Otherwise the registration event can be missed

      // Listen for registration success
      PushNotifications.addListener('registration', async (token) => {
        console.log('PushNotifications: Device token received:', token.value);
        this.deviceToken = token.value;

        // Send token to backend
        await this.sendTokenToBackend(token.value);
      });

      // Listen for registration errors
      // Capacitor liefert { error: string } (z. B. fehlendes aps-environment,
      // FCM SERVICE_NOT_AVAILABLE). Den Grund als Text loggen, sonst steht in
      // Sentry nur "[object keys: error]" und die Ursache bleibt unsichtbar.
      PushNotifications.addListener('registrationError', (error) => {
        console.error('PushNotifications: Registration error:', error?.error || error);
      });

      // Listen for push notifications received while app is in foreground
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('PushNotifications: Foreground notification:', notification);
      });

      // Listen for push notification actions (user tapped notification)
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('PushNotifications: User tapped notification:', action);
        const data = action.notification.data;

        // Handle navigation based on notification type
        this.handleNotificationTap(data);
      });

      // Request permission
      const permResult = await PushNotifications.requestPermissions();
      console.log('PushNotifications: Permission result:', permResult.receive);

      if (permResult.receive === 'granted') {
        // Register with Apple/Google push servers
        await PushNotifications.register();
        console.log('PushNotifications: Registration initiated');
      } else {
        console.warn('PushNotifications: Permission denied');
      }

      // Logout während der Initialisierung: cleanup() hat die Listener schon entfernt.
      // Nicht als initialisiert markieren, sonst übergeht der nächste Login den Aufbau.
      if (generation !== this.generation) return;
      this.isInitialized = true;
      console.log('PushNotifications: Initialization complete');

    } catch (error) {
      console.error('PushNotifications: Initialization failed:', error);
    }
  }

  /**
   * Send device token to backend
   * Backend stores token for sending push notifications
   */
  sendTokenToBackend(token) {
    const send = () => this._postToken(token);
    this.registrationQueue = this.registrationQueue.then(send, send);
    return this.registrationQueue;
  }

  async _postToken(token) {
    // Das 'registration'-Event kommt asynchron vom Betriebssystem, auch nach einem
    // Logout. Ohne Anmeldung kann das Backend das Gerät keinem Konto zuordnen (401);
    // nach Logout/Sitzungsende räumt cleanup() auf, der nächste Login registriert neu.
    // Das Token wird erst jetzt gelesen (Warteschlange): es ist immer das aktuelle Konto.
    if (!localStorage.getItem('token')) {
      console.log('PushNotifications: Kein Login, Geräte-Token wird nicht gesendet');
      return;
    }

    try {
      const platform = Capacitor.getPlatform(); // 'ios' or 'android'

      await api.post('/notifications/register-device', {
        token,
        platform
      });

      console.log('PushNotifications: Token sent to backend');
    } catch (error) {
      console.error('PushNotifications: Failed to send token to backend:', error);
    }
  }

  /**
   * Kontowechsel ohne Logout (Login/Registrierung eines anderen Kontos auf demselben
   * Gerät, z. B. nach einem Einladungslink). Aufrufen, nachdem das neue Token im
   * localStorage steht. Das Backend ordnet das Gerät dann dem neuen Konto zu und
   * deaktiviert alle anderen Zuordnungen (registerDeviceToken).
   *
   * Hintergrund (Code-Review 22.10.2026): Den Token schickte nur der
   * 'registration'-Listener nach dem ersten initialize(). Beim Wechsel ohne Logout
   * blieb das Gerät beim vorigen Konto, dessen Chat-Pushes samt Vorschau auf dem
   * Sperrbildschirm der nächsten Person landeten; das neue Konto bekam keine.
   */
  async registerForCurrentAccount() {
    if (!Capacitor.isNativePlatform()) return;
    if (this.deviceToken) {
      await this.sendTokenToBackend(this.deviceToken);
      return;
    }
    // Noch kein Geräte-Token: initialize() holt ihn, der 'registration'-Listener
    // schickt ihn dann mit dem dann gespeicherten Login.
    await this.initialize();
  }

  /**
   * Beim Logout: Device-Token im Backend deaktivieren, sonst bekommt der nächste
   * Nutzer auf demselben Gerät (Schul-iPad!) die Pushes des vorigen Kontos.
   * Muss laufen, solange der Auth-Token noch gültig ist — also VOR clearAuthStorage().
   */
  async unregisterFromBackend(authToken) {
    // Ohne Auth-Token endet der Request garantiert mit 401. Das Gerät bleibt dann dem
    // Konto zugeordnet, bis sich jemand anderes darauf anmeldet: registerDeviceToken im
    // Backend deaktiviert dabei alle fremden Zuordnungen desselben Geräte-Tokens.
    if (!Capacitor.isNativePlatform() || !this.deviceToken || !authToken) return;

    try {
      // Auth-Header explizit mitgeben: der Aufrufer (logout) räumt localStorage
      // synchron auf, der Request-Interceptor fände das Token sonst nicht mehr.
      const headers = { Authorization: `Bearer ${authToken}` };
      await api.post('/notifications/unregister-device', { token: this.deviceToken }, { headers });
      console.log('PushNotifications: Token im Backend deaktiviert');
    } catch (error) {
      console.warn('PushNotifications: Token konnte nicht deaktiviert werden:', error?.message);
    }
  }

  /**
   * Handle notification tap - navigate to relevant screen
   */
  handleNotificationTap(data) {
    // Use router navigation based on notification type
    const { type, actionUrl, chatId, sessionId } = data;

    // This will be handled by the app's navigation system
    // Emit custom event that app can listen to
    window.dispatchEvent(new CustomEvent('notification-tap', {
      detail: { type, actionUrl, chatId, sessionId }
    }));
  }

  /**
   * Get current badge count
   */
  async getBadgeCount() {
    if (!Capacitor.isNativePlatform()) return 0;

    try {
      const result = await PushNotifications.getDeliveredNotifications();
      return result.notifications.length;
    } catch (error) {
      console.error('PushNotifications: Failed to get badge count:', error);
      return 0;
    }
  }

  /**
   * Chat-Mitteilungen aus der Mitteilungszentrale entfernen (alle Chats gelesen).
   *
   * Bis Okt. 2026 entfernte das ALLE zugestellten Mitteilungen
   * (removeAllDeliveredNotifications): Einladungen und Session-Erinnerungen
   * verschwanden, sobald jemand die App ohne ungelesene Chats öffnete, obwohl die
   * Glocke sie weiter zählte (Code-Review 22.10.2026, Beitrittswege FF 3).
   * Jetzt nur noch Chat-Mitteilungen. Bleibt danach keine andere übrig, wird wie
   * bisher alles geleert, damit iOS auch das App-Badge auf 0 setzt.
   */
  async clearNotifications() {
    if (!Capacitor.isNativePlatform()) return;
    if (!this.isInitialized) return;

    try {
      const { notifications = [] } = await PushNotifications.getDeliveredNotifications();
      const chatNotifications = notifications.filter(isChatNotification);
      if (chatNotifications.length === notifications.length) {
        await PushNotifications.removeAllDeliveredNotifications();
      } else if (chatNotifications.length > 0) {
        await PushNotifications.removeDeliveredNotifications({ notifications: chatNotifications });
      }
      console.log('PushNotifications: Chat-Mitteilungen entfernt', chatNotifications.length);
    } catch (error) {
      console.error('PushNotifications: Failed to clear notifications:', error);
    }
  }

  /**
   * Cleanup - remove all listeners
   */
  async cleanup() {
    if (!Capacitor.isNativePlatform()) return;

    // Zustand SOFORT (synchron) zurücksetzen, nicht erst nach dem Entfernen der
    // Listener: Der Logout ruft cleanup() ohne Warten auf. Meldete sich das nächste
    // Konto vorher an, kam initialize() nur bis "Already initialized", danach
    // räumte cleanup() ab, und das Konto blieb bis zum Kaltstart ohne Registrierung
    // (Code-Review 22.10.2026). Der Geräte-Token selbst bleibt: Er gehört dem Gerät,
    // nicht dem Konto, und unregisterFromBackend braucht ihn nach dem cleanup().
    this.generation += 1;
    this.isInitialized = false;
    this.initPromise = null;
    const removal = PushNotifications.removeAllListeners();
    this.listenerRemoval = Promise.resolve(removal).catch(() => {});
    await removal;
    console.log('PushNotifications: Cleanup complete');
  }
}

// Export singleton instance
export default new PushNotificationService();
